/**
 * King Living Select Theme
 *
 * Global select dropdown styling system:
 * - Matches the visual style of the Field component's text inputs.
 * - Includes states for normal, focus, hover, invalid, and disabled.
 * - Custom placeholder styling.
 * - Proper icon positioning to prevent border overlap.
 *
 * @override Extends Chakra UI Select theme
 */

const Select = {
  parts: ['field', 'icon'],
  baseStyle: {
    field: {
      height: 13.5, // 54px
      px: 4.5, // 18px
      pr: 10, // 40px right padding to make room for icon
      py: 0,
      pt: 4.5, // Top padding for normal state (centers text)
      pb: 4.5, // Bottom padding for normal state (centers text)
      borderRadius: 'base', // 4px
      border: '1px solid',
      borderColor: 'brandColors.greyscale10',
      backgroundColor: 'white',
      color: 'brandColors.greyscale',
      fontSize: 'sm', // 14px
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '120%',
      transition: 'all 0.2s ease-in-out',
      _focus: {
        backgroundColor: 'white',
        borderWidth: '1.5px',
        borderColor: 'brandColors.primaryDarkLinen',
        boxShadow: 'none',
        outline: 'none',
      },
      _hover: {
        borderColor: 'brandColors.greyscale10',
        backgroundColor: 'white',
      },
      _invalid: {
        borderWidth: '1.5px',
        borderColor: 'brandColors.error',
        _focus: {
          borderColor: 'brandColors.error',
        },
      },
      _disabled: {
        backgroundColor: 'brandColors.disabledBG',
        borderWidth: '1.5px',
        borderColor: 'brandColors.greyscale10',
        color: 'brandColors.greyscale40',
        cursor: 'not-allowed',
        opacity: 1,
      },
      _placeholder: {
        color: 'brandColors.greyscale70',
        opacity: 1,
      },
    },
    icon: {
      width: 5, // 20px
      height: 5, // 20px
      right: 4.5, // 18px from right edge (matches field padding)
      color: 'brandColors.greyscale90',
      pointerEvents: 'none', // Icon is not clickable
      opacity: 1, // Ensure icon is always visible
      display: 'block', // Ensure icon is displayed
      position: 'absolute', // Ensure proper positioning
      zIndex: 1, // Ensure icon is above other elements
      visibility: 'visible', // Ensure icon is visible
      // Ensure icon remains visible in all states
      sx: {
        // Override any styles that might hide the icon
        '&': {
          opacity: 1,
          display: 'block',
          visibility: 'visible',
        },
        // Ensure icon is visible when field is focused
        '[data-focus="true"] ~ &, [data-focus="true"] + &': {
          opacity: 1,
          display: 'block',
          visibility: 'visible',
        },
      },
    },
  },
  variants: {
    outline: {
      field: {
        // Default variant, already defined in baseStyle.field
      },
    },
  },
  defaultProps: {
    variant: 'outline',
  },
};

export default Select;
